/**
 * StageConnector - Visual connection between pipeline stages showing data flow
 */

import React from 'react';
import './pipeline-animations.css';
import { ArrowRight, ArrowDown } from 'lucide-react';
import { StageType } from '@/types/execution';
import { cn } from '@/utils/cn';

interface StageConnectorProps {
  fromStage: StageType;
  toStage: StageType;
  status: 'pending' | 'active' | 'completed' | 'failed';
  orientation?: 'horizontal' | 'vertical';
  className?: string;
}

const StageConnector: React.FC<StageConnectorProps> = ({
  fromStage,
  toStage,
  status,
  orientation = 'horizontal',
  className
}) => {
  const getConnectorStyle = () => {
    switch (status) {
      case 'completed':
        return 'text-green-500 border-green-300';
      case 'active':
        return 'text-blue-500 border-blue-300';
      case 'failed':
        return 'text-red-500 border-red-300';
      case 'pending':
      default:
        return 'text-gray-400 border-gray-200';
    }
  };

  const isHorizontal = orientation === 'horizontal';
  const Arrow = isHorizontal ? ArrowRight : ArrowDown;

  return (
    <div
      className={cn(
        'flex items-center justify-center relative',
        isHorizontal ? 'flex-row' : 'flex-col',
        status === 'active' && (isHorizontal ? 'pipeline-connector-active' : 'pipeline-connector-vertical active'),
        className
      )}
      aria-label={`Connection from ${fromStage} to ${toStage}`}
    >
      {/* Connecting Line */}
      <div
        className={cn(
          'border-dashed border-2 transition-colors duration-300',
          isHorizontal ? 'flex-1 border-t-2 border-l-0 border-r-0 border-b-0' : 'flex-1 border-l-2 border-t-0 border-r-0 border-b-0',
          getConnectorStyle()
        )}
      />

      {/* Arrow Icon */}
      <div className={cn('flex-shrink-0 mx-2', getConnectorStyle())}>
        <Arrow className="w-4 h-4" />
      </div>

      {/* Connecting Line */}
      <div
        className={cn(
          'border-dashed border-2 transition-colors duration-300',
          isHorizontal ? 'flex-1 border-t-2 border-l-0 border-r-0 border-b-0' : 'flex-1 border-l-2 border-t-0 border-r-0 border-b-0',
          getConnectorStyle()
        )}
      />

      {/* Animated Flow Effect for Active Connections */}
      {status === 'active' && (
        <div className="absolute inset-0 overflow-hidden">
          <div
            className={cn(
              'absolute bg-blue-400 opacity-60 rounded-full',
              isHorizontal ? 'w-2 h-2 animate-pulse' : 'w-2 h-2 animate-bounce'
            )}
            style={{
              animation: isHorizontal 
                ? 'flowHorizontal 2s infinite linear' 
                : 'flowVertical 2s infinite linear'
            }}
          />
        </div>
      )}

      {/* Data Flow Label (visible on hover) */}
      <div className="absolute opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10">
        <div className="bg-gray-800 text-white text-xs px-2 py-1 rounded whitespace-nowrap">
          {fromStage} → {toStage}
        </div>
      </div>

      <style>{`
        @keyframes flowHorizontal {
          0% { left: 0%; }
          100% { left: 100%; }
        }
        @keyframes flowVertical {
          0% { top: 0%; }
          100% { top: 100%; }
        }
      `}</style>
    </div>
  );
};

export default StageConnector;